import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AnimatePresence, motion } from 'framer-motion';

import { Header, Sidebar } from './components/Header';
import Dashboard from './components/Dashboard';
import AssetsView from './components/AssetsView';
import BridgeView from './components/BridgeView';
import AvatarView from './components/AvatarView';
import SettingsView from './components/SettingsView';
import ErrorBoundary from './components/ErrorBoundary';
import { bridgeAPI } from './services/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

function AppRoutes({
  isConnected,
  userAddress,
  bridgeStatus,
  sidebarOpen,
  setSidebarOpen
}) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Routes location={location}>
          <Route
            path="/"
            element={
              <Dashboard
                userAddress={userAddress}
                bridgeStatus={bridgeStatus}
                onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              />
            }
          />
          <Route path="/assets" element={<AssetsView userAddress={userAddress} />} />
          <Route path="/bridge" element={<BridgeView userAddress={userAddress} />} />
          <Route path="/avatar" element={<AvatarView userAddress={userAddress} />} />
          <Route path="/settings" element={<SettingsView userAddress={userAddress} />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [userAddress, setUserAddress] = useState('');
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    checkBridgeStatus();
  }, []);

  const checkBridgeStatus = async () => {
    try {
      const response = await bridgeAPI.getBridgeStatus();
      setBridgeStatus(response?.data ?? response);
    } catch (error) {
      console.error('Failed to check bridge status:', error);
    }
  };

  const connectWallet = async () => {
    if (typeof window !== 'undefined' && window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setUserAddress(accounts[0]);
        setIsConnected(true);
      } catch (error) {
        console.error('Failed to connect wallet:', error);
      }
    } else {
      const idUrl = process.env.REACT_APP_GALAXIA_ID_URL || 'https://galaxia-id.vercel.app';
      window.open(idUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const disconnectWallet = () => {
    setUserAddress('');
    setIsConnected(false);
    localStorage.removeItem('galaxia_jwt');
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen" style={{ background: 'var(--galaxia-bg)' }}>
          <div className="galaxia-starfield" aria-hidden="true" />
          <Header
            isConnected={isConnected}
            userAddress={userAddress}
            onConnect={connectWallet}
            onDisconnect={disconnectWallet}
            bridgeStatus={bridgeStatus}
          />

          <div className="flex">
            <Sidebar
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              isConnected={isConnected}
            />

            <main className="flex-1 p-6 relative z-10 max-w-7xl mx-auto w-full">
              <ErrorBoundary fallbackTitle="Main view failed to load">
                <AppRoutes
                  isConnected={isConnected}
                  userAddress={userAddress}
                  bridgeStatus={bridgeStatus}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                />
              </ErrorBoundary>
            </main>
          </div>
        </div>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
